import { create } from 'zustand';

/**
 * Surfaces currently asking the shell's nav rail to show as icons only.
 *
 * The file preview panel is the one caller (spec 140 FR-078). It is pinned to
 * the right and takes most of the width, and at its first geometry it reached
 * the viewport's left edge: the rail was covered completely, so the preview
 * read as a new page rather than as something open *over* the page the reader
 * was on. Minifying the rail — rather than letting it be hidden — keeps the
 * app frame on screen and keeps the preview legible as a preview.
 *
 * A **count**, not a boolean: two surfaces could ask at once (a deep link that
 * renders the panel while another is unmounting), and the last unmount must not
 * restore the rail while someone still wants it minified.
 *
 * Deliberately **not** `preferences`' `sidebarCollapsed`: that is the reader's
 * own stored choice, and writing to it would make opening one preview change
 * how the app looks on every later visit. This is memory only and nothing about
 * it outlives the page — the same reasoning as `focusMode`.
 */
interface RailMinifyState {
  /** How many open surfaces are asking. `> 0` means "show icons only". */
  requests: number;
  /** Ask; returns nothing. Pair every call with `release`. */
  request: () => void;
  release: () => void;
}

export const useRailMinifyStore = create<RailMinifyState>((set) => ({
  requests: 0,
  request: () => {
    set((state) => ({ requests: state.requests + 1 }));
  },
  release: () => {
    // Floored at zero: a stray release must not take the count negative and
    // leave a later request unable to reach `> 0`.
    set((state) => ({ requests: Math.max(0, state.requests - 1) }));
  },
}));
